import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/api';
import { Bell, RefreshCw, CheckCircle2, XCircle, MinusCircle, AlertTriangle } from 'lucide-react';
import ProcessingStatus, { dimClass } from './ProcessingStatus';
import { Skel, SkelRows } from './Skeleton';
import { useLatestRequest, isCanceled } from '../utils/latestRequest';

// Notification delivery history — reads the engine's notification_log audit
// table so anyone (clients included, permission-filtered server-side) can see
// whether a notification was actually sent, on which channel, and when.

const KIND_LABEL = {
  action_plan: 'Action plan',
  flip: 'Long alert',
  short: 'Short alert',
};
const KIND_STYLE = {
  action_plan: 'bg-sky-500/15 text-sky-300 border-sky-400/40',
  flip: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/40',
  short: 'bg-orange-500/15 text-orange-300 border-orange-400/40',
};
const GROUP_LABEL = { asia: 'Asia', crypto: 'Crypto', india: 'India', europe: 'Europe', us: 'US' };

const ChannelBadge = ({ status }) => {
  if (status === 'ok') {
    return <span className="inline-flex items-center gap-1 text-green-400"><CheckCircle2 size={12} />ok</span>;
  }
  if (status === 'partial') {
    return <span className="inline-flex items-center gap-1 text-amber-400"><AlertTriangle size={12} />partial</span>;
  }
  if (status === 'failed') {
    return <span className="inline-flex items-center gap-1 text-red-400"><XCircle size={12} />failed</span>;
  }
  return <span className="inline-flex items-center gap-1 text-dark-500"><MinusCircle size={12} />—</span>;
};

const NotificationHistory = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kind, setKind] = useState('');
  const [market, setMarket] = useState('');
  const [days, setDays] = useState(7);
  const [failuresOnly, setFailuresOnly] = useState(false);
  const mounted = useRef(true);

  const rowsReq = useLatestRequest();
  const fetchRows = async () => {
    const req = rowsReq();          // a newer filter click cancels this one
    try {
      setLoading(true);
      setError(null);
      const params = { days };
      if (kind) params.kind = kind;
      if (market) params.market = market;
      if (failuresOnly) params.failures = 1;
      const res = await apiService.getNotificationHistory(params, { signal: req.signal });
      if (mounted.current && req.isCurrent()) setData(res.data);
    } catch (e) {
      if (mounted.current && req.isCurrent() && !isCanceled(e)) setError('Failed to load notification history.');
    } finally {
      if (mounted.current && req.isCurrent()) setLoading(false);
    }
  };

  useEffect(() => {
    mounted.current = true;
    fetchRows();
    return () => { mounted.current = false; };
  }, [kind, market, days, failuresOnly]);

  const rows = data?.rows || [];

  return (
    <div className="dashboard-card">
      {/* Header + filters */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary-400" />
          <h3 className="text-xl font-semibold text-white">Notifications</h3>
          {!data && loading && <Skel className="h-6 w-32" testId="notif-count-pending" />}
          {data && (
            <span className="text-xs text-dark-400 bg-dark-700/60 rounded-md px-2 py-1">
              {data.total} in last {data.days}d
              {data.failures > 0 && <span className="text-red-400"> · {data.failures} failed</span>}
              {data.failures === 0 && data.total > 0 && <span className="text-green-400"> · all delivered</span>}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={kind} onChange={e => setKind(e.target.value)}
            className="bg-dark-700 border border-dark-600 text-dark-200 text-xs rounded-lg px-2 py-1.5">
            <option value="">All types</option>
            <option value="action_plan">Action plans</option>
            <option value="flip">Long alerts</option>
            <option value="short">Short alerts</option>
          </select>
          <select value={market} onChange={e => setMarket(e.target.value)}
            className="bg-dark-700 border border-dark-600 text-dark-200 text-xs rounded-lg px-2 py-1.5">
            <option value="">All markets</option>
            <option value="asia">Asia</option>
            <option value="crypto">Crypto</option>
            <option value="india">India</option>
            <option value="europe">Europe</option>
            <option value="us">US</option>
          </select>
          <select value={days} onChange={e => setDays(parseInt(e.target.value, 10))}
            className="bg-dark-700 border border-dark-600 text-dark-200 text-xs rounded-lg px-2 py-1.5">
            <option value={1}>Last day</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <label className="flex items-center gap-1.5 text-xs text-dark-300 cursor-pointer">
            <input type="checkbox" checked={failuresOnly} onChange={e => setFailuresOnly(e.target.checked)}
              className="accent-red-500" />
            Failures only
          </label>
          <button onClick={fetchRows}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs font-medium transition-colors">
            <RefreshCw className="w-3 h-3" />
            Refresh
          </button>
        </div>
      </div>

      {/* Proof at a glance: was today's plan sent for each market? */}
      {!data && loading && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="plans-pending">
          <span className="text-dark-400 text-[10px] uppercase tracking-widest font-semibold mr-1">Today's plans</span>
          {[0, 1, 2, 3, 4].map(i => <Skel key={i} className="h-[26px] w-24" rounded="rounded-full" />)}
        </div>
      )}
      {data?.today_plans?.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-dark-400 text-[10px] uppercase tracking-widest font-semibold mr-1">Today's plans</span>
          {data.today_plans.map(p => (
            <button key={p.market} onClick={() => setMarket(market === p.market ? '' : p.market)}
              title={p.status === 'sent' ? `Plan delivered at ${new Date(p.at).toLocaleTimeString('en-GB', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit', hour12: true })} IST — click to filter`
                : p.status === 'failed' ? 'Delivery attempts failed — click to inspect'
                : 'Not sent yet — sends before this market opens'}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] transition-colors ${market === p.market ? 'ring-1 ring-primary-400' : ''} ${
                p.status === 'sent' ? 'border-green-500/40 text-green-300 bg-green-500/10'
                : p.status === 'failed' ? 'border-red-500/40 text-red-300 bg-red-500/10'
                : 'border-dark-600 text-dark-400 bg-dark-800/60'}`}>
              {p.status === 'sent' ? <CheckCircle2 size={11} /> : p.status === 'failed' ? <XCircle size={11} /> : <MinusCircle size={11} />}
              <span className="font-medium">{GROUP_LABEL[p.market]}</span>
              <span className="opacity-80">{p.status === 'sent'
                ? new Date(p.at).toLocaleTimeString('en-GB', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit', hour12: true })
                : p.status}</span>
            </button>
          ))}
        </div>
      )}

      <ProcessingStatus active={loading} title="Loading notifications" />
      {error && <div className="text-red-400 text-sm mb-3">{error}</div>}

      {!loading && rows.length === 0 && !error && (
        <div className="text-center py-10 text-dark-500">
          {failuresOnly ? 'No failed notifications — everything delivered.' : 'No notifications in this period.'}
        </div>
      )}

      {(rows.length > 0 || (loading && !data)) && (
        <div className={`overflow-x-auto ${dimClass(loading && rows.length > 0)}`}>
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-dark-400 border-b border-dark-700">
                <th className="py-2 px-2">Sent (IST)</th>
                <th className="py-2 px-2">Type</th>
                <th className="py-2 px-2">Market / Index</th>
                <th className="py-2 px-2">Delivered</th>
                <th className="py-2 px-2">Telegram</th>
                <th className="py-2 px-2">Email</th>
                <th className="py-2 px-2">Message</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data && <SkelRows cols={7} cellClass="py-2 px-2" />}
              {rows.map((r, i) => (
                <tr key={i} className={`border-b border-dark-700/50 text-sm ${!r.delivered ? 'bg-red-900/15' : ''}`}>
                  <td className="py-2 px-2 text-dark-300 whitespace-nowrap">
                    {new Date(r.at).toLocaleString('en-GB', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true })}
                  </td>
                  <td className="py-2 px-2">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold border ${KIND_STYLE[r.kind] || 'bg-dark-700 text-dark-300 border-dark-600'}`}>
                      {KIND_LABEL[r.kind] || r.kind}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-dark-200">
                    {r.kind === 'action_plan' ? (GROUP_LABEL[r.market_group] || r.market_group) : r.symbol}
                  </td>
                  <td className="py-2 px-2 text-xs whitespace-nowrap">
                    <span className={`font-semibold ${r.delivered ? 'text-green-400' : 'text-red-400'}`}>{r.delivered_count}/{r.recipients}</span>
                    {r.failed_to?.length > 0 && (
                      <div className="text-red-400/90 text-[10px] mt-0.5" title={`Delivery failed for: ${r.failed_to.join(', ')}`}>
                        failed: {r.failed_to.join(', ')}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-2 text-xs"><ChannelBadge status={r.telegram} /></td>
                  <td className="py-2 px-2 text-xs"><ChannelBadge status={r.email} /></td>
                  <td className="py-2 px-2 text-xs text-dark-400 max-w-[280px] truncate" title={r.preview}>{r.preview}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NotificationHistory;
